import React, { useState } from "react";
import { useRole } from "@/hooks/useRole";
import { Loader2, Filter } from "lucide-react";
import { AccessDenied } from "@/components/admin/games/AccessDenied";
import { UsersPageHeader } from "@/components/admin/users/UsersPageHeader";
import { UserSearchBar } from "@/components/admin/users/UserSearchBar";
import { UsersLoadingState } from "@/components/admin/users/UsersLoadingState";
import { UsersList } from "@/components/admin/users/UsersList";
import { useUsersAdmin } from "@/hooks/useUsersAdmin";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const UsuariosAdmin = () => {
  const { role, loading: roleLoading } = useRole();
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const {
    users,
    loading,
    searchTerm,
    setSearchTerm,
    openEditDialog,
    updateUserRole,
    refreshUsers
  } = useUsersAdmin();

  if (roleLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0f141c] pt-16">
        <Loader2 className="h-12 w-12 text-[#00B6F0] animate-spin" />
      </div>
    );
  }

  if (role !== "admin") {
    return <AccessDenied />;
  }

  // Filtrar usuários por role
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.nickname.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin': return 'Administradores';
      case 'mentor': return 'Mentores';
      case 'jogador': return 'Jogadores';
      default: return 'Todos';
    }
  };

  const getRoleCount = (role: string) => {
    if (role === 'all') return users.length;
    return users.filter(user => user.role === role).length;
  };

  return (
    <div className="bg-[#0f141c] min-h-screen pt-16 p-8">
      <div className="max-w-6xl mx-auto">
        <UsersPageHeader totalUsers={users.length} />

        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <UserSearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
          </div>
          
          {/* Filtro por Role */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="bg-[#1E293B] border-[#2A3749] text-white hover:bg-[#2A3749]">
                <Filter className="mr-2 h-4 w-4" />
                {getRoleLabel(roleFilter)}
                <Badge variant="outline" className="ml-2 text-xs">
                  {getRoleCount(roleFilter)}
                </Badge>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[#1E293B] border-[#2A3749] text-white">
              <DropdownMenuItem 
                onClick={() => setRoleFilter('all')}
                className="hover:bg-[#2A3749] cursor-pointer"
              >
                Todos ({users.length})
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => setRoleFilter('admin')}
                className="hover:bg-[#2A3749] cursor-pointer"
              >
                Administradores ({getRoleCount('admin')})
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => setRoleFilter('mentor')}
                className="hover:bg-[#2A3749] cursor-pointer"
              >
                Mentores ({getRoleCount('mentor')})
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => setRoleFilter('jogador')}
                className="hover:bg-[#2A3749] cursor-pointer"
              >
                Jogadores ({getRoleCount('jogador')})
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {loading ? (
          <UsersLoadingState />
        ) : (
          <UsersList 
            users={filteredUsers} 
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            onEdit={openEditDialog} 
            onChangeRole={updateUserRole}
            onCommissionUpdated={refreshUsers}
          />
        )}
      </div>
    </div>
  );
};

export default UsuariosAdmin;
